/**
 * Label template LAYOUT (label_templates.layout_json). The single source of
 * validation for the label designer (settings · Formats d'etiqueta) and — later
 * — the label generator/renderer.
 *
 * DESIGN NOTES
 * - Units are MILLIMETRES, not pixels. The on-screen preview scales mm→px, and a
 *   future thermal (ZPL) / PDF renderer needs physical units to stay faithful.
 * - The model is a VERTICAL STACK of blocks (top → bottom), each full width with
 *   its own alignment + type size. This maps cleanly to both an HTML/SVG preview
 *   and a future ZPL/PDF translator; free x/y positioning is a later enhancement,
 *   hence the explicit `version` for forward migration.
 * - A block's `field` selects WHICH datum it shows (resolved at generation time
 *   from the finished_lot / tech_sheet / article / tenant). qr/barcode/logo/
 *   image/spacer/custom_text are presentation blocks. `caption` is an optional
 *   prefix (e.g. "Lot:") — or, for custom_text, the literal content.
 */

import { z } from 'zod';
import { LABEL_FIELDS, LABEL_TYPES, type LabelField, type LabelType } from '../enums/index.js';

export const LABEL_ALIGNS = ['left', 'center', 'right'] as const;
export type LabelAlign = (typeof LABEL_ALIGNS)[number];

export const LabelBlock = z
  .object({
    field: z.enum(LABEL_FIELDS),
    /** Optional caption prefix (e.g. "Lot:"); the literal text for custom_text. */
    caption: z.string().trim().max(120).optional(),
    /** Type size in points (screen + print). */
    font_pt: z.number().finite().min(5).max(48),
    bold: z.boolean(),
    align: z.enum(LABEL_ALIGNS),
  })
  .strict();
export type LabelBlock = z.infer<typeof LabelBlock>;

export const LabelLayout = z
  .object({
    /** Schema version — bump on any breaking layout-model change. */
    version: z.literal(1),
    width_mm: z.number().finite().min(20).max(300),
    height_mm: z.number().finite().min(15).max(300),
    padding_mm: z.number().finite().min(0).max(20),
    show_border: z.boolean(),
    /** Ordered top → bottom. */
    blocks: z.array(LabelBlock).max(40),
  })
  .strict();
export type LabelLayout = z.infer<typeof LabelLayout>;

/** Per-field metadata: which label types it belongs to + legal requirement. */
export interface LabelFieldMeta {
  /** Types this field is offered for in the editor. */
  readonly types: readonly LabelType[];
  /** Mandatory on a sales_legal label (EU Reg. 1169/2011) — a warning if absent. */
  readonly mandatorySalesLegal: boolean;
}

const BOTH: readonly LabelType[] = LABEL_TYPES;
const SALES: readonly LabelType[] = ['sales_legal'];
const INTERNAL: readonly LabelType[] = ['internal_lot'];

export const LABEL_FIELD_META: Record<LabelField, LabelFieldMeta> = {
  legal_denomination: { types: SALES, mandatorySalesLegal: true },
  product_name: { types: BOTH, mandatorySalesLegal: false },
  ingredients: { types: SALES, mandatorySalesLegal: true },
  allergens: { types: SALES, mandatorySalesLegal: true },
  nutrition: { types: SALES, mandatorySalesLegal: false },
  net_weight: { types: SALES, mandatorySalesLegal: true },
  conservation: { types: SALES, mandatorySalesLegal: false },
  country_of_origin: { types: SALES, mandatorySalesLegal: false },
  business_name: { types: SALES, mandatorySalesLegal: true },
  business_address: { types: SALES, mandatorySalesLegal: true },
  sanitary_registry: { types: SALES, mandatorySalesLegal: false },
  lot_number: { types: BOTH, mandatorySalesLegal: true },
  production_date: { types: BOTH, mandatorySalesLegal: false },
  expiry_date: { types: BOTH, mandatorySalesLegal: false },
  best_before: { types: SALES, mandatorySalesLegal: false },
  quantity: { types: INTERNAL, mandatorySalesLegal: false },
  sku: { types: BOTH, mandatorySalesLegal: false },
  custom_text: { types: BOTH, mandatorySalesLegal: false },
  qr: { types: BOTH, mandatorySalesLegal: false },
  barcode: { types: SALES, mandatorySalesLegal: false },
  logo: { types: BOTH, mandatorySalesLegal: false },
  image: { types: BOTH, mandatorySalesLegal: false },
  spacer: { types: BOTH, mandatorySalesLegal: false },
};

/** Fields offered in the editor for a given label type, in catalogue order. */
export function labelFieldsForType(type: LabelType): readonly LabelField[] {
  return LABEL_FIELDS.filter((f) => LABEL_FIELD_META[f].types.includes(type));
}

/** The mandatory sales_legal fields missing from a layout (for a soft warning). */
export function missingMandatoryFields(
  type: LabelType,
  layout: LabelLayout,
): readonly LabelField[] {
  if (type !== 'sales_legal') return [];
  const present = new Set(layout.blocks.map((b) => b.field));
  return LABEL_FIELDS.filter(
    (f) => LABEL_FIELD_META[f].mandatorySalesLegal && !present.has(f),
  );
}

const block = (
  field: LabelField,
  font_pt: number,
  opts: { bold?: boolean; align?: LabelAlign; caption?: string } = {},
): LabelBlock => ({
  field,
  font_pt,
  bold: opts.bold ?? false,
  align: opts.align ?? 'left',
  ...(opts.caption !== undefined ? { caption: opts.caption } : {}),
});

/**
 * A sensible starting layout for a new template of the given type: a small
 * traceability sticker for internal_lot, a fuller 1169/2011 label for sales_legal.
 */
export function defaultLabelLayout(type: LabelType): LabelLayout {
  if (type === 'internal_lot') {
    return {
      version: 1,
      width_mm: 60,
      height_mm: 40,
      padding_mm: 3,
      show_border: true,
      blocks: [
        block('product_name', 11, { bold: true, align: 'center' }),
        block('lot_number', 9, { caption: 'Lot' }),
        block('production_date', 8, { caption: 'Prod.' }),
        block('expiry_date', 8, { caption: 'Cad.' }),
        block('qr', 8, { align: 'center' }),
      ],
    };
  }
  return {
    version: 1,
    width_mm: 100,
    height_mm: 70,
    padding_mm: 4,
    show_border: true,
    blocks: [
      block('legal_denomination', 12, { bold: true }),
      block('ingredients', 7),
      block('allergens', 7, { bold: true }),
      block('net_weight', 9),
      block('best_before', 8, { caption: 'Consum preferent' }),
      block('lot_number', 8, { caption: 'Lot' }),
      block('conservation', 7),
      block('business_name', 7),
      block('business_address', 7),
    ],
  };
}
